import { useEffect, useState } from 'react';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { getPlatformPermissions } from './api';
import { useIdentity } from './useIdentity';

export interface PlatformPermissionsState {
  canDelete: boolean;
  canEdit: boolean;
}

/**
 * What the signed-in user is allowed to do, as answered by the server. The
 * frontend never holds its own copy of the delete allowlist — see
 * platform-controller/src/applications/delete-allowlist.ts, the single source
 * of truth.
 *
 * Fails closed: any error, or an identity that hasn't resolved yet, leaves
 * `canDelete` false. The failure direction hides the button rather than
 * showing one that would 403.
 */
export function usePlatformPermissions(): PlatformPermissionsState {
  const identityApi = useApi(identityApiRef);
  const { email } = useIdentity();
  const [canDelete, setCanDelete] = useState(false);
  const [canEdit, setCanEdit] = useState(false);

  useEffect(() => {
    if (!email) return;
    let cancelled = false;
    getPlatformPermissions(identityApi, email)
      .then((perms) => {
        if (!cancelled) {
          setCanDelete(perms.canDelete);
          setCanEdit(perms.canEdit);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCanDelete(false);
          setCanEdit(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [identityApi, email]);

  return { canDelete, canEdit };
}
